import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Entity } from '@backstage/catalog-model';
import { useEffect, useState } from 'react';

export interface ProductPickerProps {
  placeholder?: string;
  label: string;
  productCode: string;
  onValueChange: (newProduct: ProductInfo | null) => void;
}

export interface ProductInfo {
  name: string;
  code: string | undefined;
}

function productFromEntity(entity: Entity | null): ProductInfo | null {
  const name = (entity?.metadata.title || entity?.metadata.name) ?? 'Unknown';
  const code = getProductCode(entity);
  if (!code) {
    return null;
  }
  return {
    name,
    code,
  };
}

export function getProductCode(context: any) {
  return context?.metadata?.labels?.['accessacloud.com/reporting-code'];
}

function useProductEntities() {
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entities,
    loading,
    error,
  } = useAsync(() => {
    return catalogApi.getEntities({
      filter: {
        kind: 'System',
      },
    });
  });

  return {
    loading,
    error,
    entities,
  };
}

export const ProductPicker = (props: ProductPickerProps) => {
  const { onValueChange, productCode } = props;
  const { loading, error, entities } = useProductEntities();
  const [initial, setInitial] = useState<string | undefined>(productCode);

  const filteredEntities = entities?.items ?? [];
  const selectedEntity =
    filteredEntities.find(
      it => productCode && getProductCode(it) === productCode,
    ) ?? null;

  useEffect(() => {
    if (selectedEntity && initial === productCode) {
      onValueChange(productFromEntity(selectedEntity));
      setInitial(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error occurred: {error.message}</div>;

  return (
    <Autocomplete
      fullWidth
      value={selectedEntity}
      getOptionLabel={o => o.metadata.title || o.metadata.name}
      onChange={(_, newValue) => onValueChange(productFromEntity(newValue))}
      options={filteredEntities}
      renderInput={params => <TextField {...params} label="Products" />}
    />
  );
};
